import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const STATUS_COLORS = {
  at_warehouse: '#0369a1',
  in_transit: '#7c3aed',
  arrived_ghana: '#16a34a',
  ready_for_pickup: '#0f766e',
  out_for_delivery: '#d97706',
  delivered: '#15803d'
};

export default function AdminAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get('/api/admin/analytics').then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="loading-center"><div className="spinner spinner-dark" /></div>;

  const revenueData = (data.monthlyRevenue || []).reverse().map(r => ({ month: r.month?.substring(5), revenue: parseFloat(r.revenue) }));
  const pieData = (data.statusBreakdown || []).map(s => ({ name: s.status?.replace(/_/g, ' '), value: s.count, color: STATUS_COLORS[s.status] || '#9ca3af' }));

  return (
    <div>
      <div className="page-header">
        <Link to="/admin" className="back-btn">←</Link>
        <h1>Analytics 📈</h1>
      </div>
      <div className="page-content">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { label: 'Total Revenue', value: `GHS ${parseFloat(data.totalRevenue || 0).toLocaleString('en-GH', { minimumFractionDigits: 0 })}`, icon: '💰' },
            { label: 'Total Shipments', value: data.totalShipments || 0, icon: '📦' },
            { label: 'Customers', value: data.totalCustomers || 0, icon: '👥' },
            { label: 'Unpaid Items', value: data.unpaidItems || 0, icon: '⏳' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <p style={{ fontSize: 22 }}>{s.icon}</p>
              <p className="value">{s.value}</p>
              <p className="label">{s.label}</p>
            </div>
          ))}
        </div>

        {revenueData.length > 0 && (
          <div className="card" style={{ marginTop: 8 }}>
            <p style={{ fontWeight: 700, marginBottom: 12 }}>Monthly Revenue (GHS)</p>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={revenueData}>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `GHS ${v.toLocaleString()}`} />
                <Bar dataKey="revenue" fill="#1A6B8A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {pieData.length > 0 && (
          <div className="card">
            <p style={{ fontWeight: 700, marginBottom: 12 }}>Shipment Status Breakdown</p>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {data.recentPayments?.length > 0 && (
          <>
            <p className="section-title" style={{ marginTop: 8 }}>Recent Verified Payments</p>
            {data.recentPayments.map((p, i) => (
              <div key={i} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontWeight: 600, fontSize: 14 }}>{p.full_name}</p>
                  <p style={{ fontSize: 12, color: '#6b7280' }}>{p.shipping_mark} · {new Date(p.created_at).toLocaleDateString()}</p>
                </div>
                <p style={{ fontWeight: 800, color: '#16a34a' }}>GHS {parseFloat(p.amount).toFixed(2)}</p>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
